declare let google: any;

export interface GeoCoordinates {
  x: number;
  y: number;
}

export interface GeoLocationDetails extends GeoCoordinates {
  zipCode: string;
  cityname: string;
  stateabbr: string;
}
// Cache for memoizing geocoding results
const locationCache = new Map<string, GeoLocationDetails | null>();

// Clears the location cache (useful for testing)
export function clearLocationCache(): void {
  locationCache.clear();
}

// Extracts coordinates and location details from Google Geocoder results
export function extractDetailsFromResults(results: any[]): GeoLocationDetails | null {
  const zipResult = results.find((r) => r.types?.[0] === 'postal_code');
  const components = zipResult?.address_components;

  if (!zipResult || !components || components.length === 0) {
    return null;
  }

  const zipCode = components.find((c: any) => c.types.includes('postal_code'))?.short_name;
  const cityname = components.find((c: any) => c.types.includes('political'))?.long_name;
  const stateabbr = components.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name;

  if (zipCode && cityname && stateabbr) {
    return {
      zipCode,
      x: zipResult.geometry.location.lng(),
      y: zipResult.geometry.location.lat(),
      cityname,
      stateabbr,
    };
  }

  return null;
}

/**
 * Converts a US zipcode to geographic coordinates using Google Geocoder
 * Returns full location details including city and state
 * Results are memoized to avoid duplicate API calls for the same zipcode
 */
export function zipToLocationDetails(zipCode: string): Promise<GeoLocationDetails | null> {
  // Return cached result if available
  if (locationCache.has(zipCode)) {
    return Promise.resolve(locationCache.get(zipCode) ?? null);
  }

  return new Promise((resolve, reject) => {
    new google.maps.Geocoder().geocode(
      { address: zipCode, componentRestrictions: { country: 'US' } },
      (results: any[], status: string) => {
        if (status === 'OK') {
          const locationDetails = extractDetailsFromResults(results);
          locationCache.set(zipCode, locationDetails);
          resolve(locationDetails);
        } else {
          reject(new Error('Geocoding failed'));
        }
      }
    );
  });
}
